import { nativeFetch, nativeSendBeacon, nativeXhrOpen } from "../core/native";
import { log, type BlockingContext } from "./context";

/**
 * Catches trackers that never touch the DOM: `fetch`/XHR/`sendBeacon` calls made by an
 * already-loaded first-party bundle, and tags proxied through the site's own origin
 * (server-side GTM, Cloudflare Zaraz, HubSpot). Without this layer, a "first-party"
 * analytics endpoint would sail straight past the element guards.
 *
 * Blocked calls resolve *safely* rather than throwing — a consent tool that turns a
 * tracker into an unhandled rejection has broken the host page.
 */
export function installNetworkGuard(context: BlockingContext): void {
  patchFetch(context);
  patchXhr(context);
  patchSendBeacon(context);
}

function shouldBlock(url: string, context: BlockingContext): boolean {
  const category = context.blocklist.categoryFor(url);
  return category !== null && !context.isGranted(category);
}

function patchFetch(context: BlockingContext): void {
  const original = nativeFetch;
  if (!original) return;

  window.fetch = function patched(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = urlFromRequestInfo(input);
    if (url && shouldBlock(url, context)) {
      log(context, "blocked fetch:", url);
      return Promise.resolve(new Response(null, { status: 204, statusText: "No Content" }));
    }
    return original(input, init);
  };
}

function patchXhr(context: BlockingContext): void {
  if (!nativeXhrOpen) return;
  const nativeOpen = nativeXhrOpen;
  const nativeSend = XMLHttpRequest.prototype.send;
  const BLOCKED = "__ckBlocked";

  type GuardedXhr = XMLHttpRequest & { [BLOCKED]?: boolean };

  XMLHttpRequest.prototype.open = function patched(
    this: GuardedXhr,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    const target = String(url);
    this[BLOCKED] = shouldBlock(target, context);
    if (this[BLOCKED]) log(context, "blocked XHR:", target);
    return (nativeOpen as (...args: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function patched(this: GuardedXhr, body?: unknown) {
    if (this[BLOCKED]) {
      // Mimic a network failure: the same shape a tracker already handles when offline.
      setTimeout(() => {
        this.dispatchEvent(new ProgressEvent("error"));
        this.dispatchEvent(new ProgressEvent("loadend"));
      }, 0);
      return;
    }
    return (nativeSend as (this: XMLHttpRequest, body?: unknown) => void).call(this, body);
  } as typeof XMLHttpRequest.prototype.send;
}

function patchSendBeacon(context: BlockingContext): void {
  const original = nativeSendBeacon;
  if (!original) return;

  navigator.sendBeacon = function patched(url: string | URL, data?: BodyInit | null): boolean {
    if (shouldBlock(String(url), context)) {
      log(context, "blocked sendBeacon:", String(url));
      return false;
    }
    return original(url, data);
  };
}

function urlFromRequestInfo(input: RequestInfo | URL): string | null {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return null;
}
